import { ROLE_MENTOR, ROLE_USER } from 'constants/roles';
import { ROUTE_MENTOR_PATH, ROUTE_PATH, ROUTE_STARTUP_PATH } from 'routes';
import { WEBIEW_MESSAGE } from 'constants/webview';
import { sendMessageToWebview } from 'utils/sendMessageToWebview';
import { createFund, editFund } from 'components/FundSearcher/modules/actions';
import { createInvestmentAsset, updateInvestmentAsset } from 'modules/investmentAssets/repository';
import {
  startupCreateAction,
  mentorCreateAction,
  editStartup,
} from '../modules/actions';

const createOnboardingConfig = ({
  id,
  user,
  startup,
  investmentAsset,
  showLoading,
  navigate,
  fetchCompany,
  embed,
  dialog,
  literals,
}) => {
  const handleError = async (error) => {
    const errorCodes = {
      lie_already_exist: literals.lieAlreadyExist,
      lie_already_exist_verified: literals.lieAlreadyExistVerified,
    };
    const errMsg = errorCodes[error.body?.error?.code] || literals.genericError || 'Error';
    await dialog({ type: 'error', text: errMsg });
  };

  const handleShowLoading = async (callback) => {
    if (showLoading) {
      showLoading(true);
    }
    try {
      return await callback();
    } catch (error) {
      await handleError(error);
      return null;
    } finally {
      if (showLoading) {
        showLoading(false);
      }
    }
  };

  const handleStartupSubmit = async (data) => {
    let startupId;
    if (id) {
      await editStartup(id, data);
      startupId = id;
    } else {
      const response = await startupCreateAction(data);
      startupId = response.id;
    }

    const startupData = await fetchCompany(startupId);

    if (embed) {
      sendMessageToWebview(WEBIEW_MESSAGE.NEW_STARTUP, startupData);
    } else {
      navigate({ pathname: ROUTE_STARTUP_PATH.setHome(startupId) });
    }
  };

  const handleFundSubmit = async (data) => {
    if (id) {
      await editFund(id, data);
      return;
    }

    const response = await createFund(data);
    if (embed) {
      sendMessageToWebview(WEBIEW_MESSAGE.NEW_FUND, {
        id: response.id,
        name: response.name,
        logo: response.logo,
        verified: response.verified,
      });
    } else {
      navigate({ pathname: ROUTE_PATH.PREHOME });
    }
  };

  const handleInvestmentAssetSubmit = async (auxData, type, orgs) => {
    const data = { ...auxData, type, organization: orgs.join(',') };
    if (data.id) {
      await updateInvestmentAsset(id, data);
      return;
    }

    const response = await createInvestmentAsset(data);
    if (embed) {
      sendMessageToWebview(WEBIEW_MESSAGE.NEW_INVESTMENT_ASSET, {
        id: response.id,
        name: response.name,
        logo: response.logo,
        verified: response.verified,
      });
    }
  };

  return {
    company_founder: {
      roleCheck: ROLE_USER,
      onFinish: async (data) => handleShowLoading(() => handleStartupSubmit(data)),
      getAnswers: () => (id ? startup?.company : null),
    },
    company_guest: {
      roleCheck: ROLE_USER,
      onFinish: async (data) => handleShowLoading(() => handleStartupSubmit(data)),
      getAnswers: () => (id ? startup?.company : null),
    },

    fund_founder: {
      roleCheck: ROLE_USER,
      onFinish: async (data) => handleShowLoading(() => handleFundSubmit(data)),
      getAnswers: () => investmentAsset || null,
    },

    investment: {
      roleCheck: ROLE_USER,
      onFinish: async (data, type, orgs) => handleShowLoading(() => handleInvestmentAssetSubmit(data, type, orgs)),
      getAnswers: () => investmentAsset || null,
    },

    mentor: {
      roleCheck: ROLE_MENTOR,
      onFinish: async (data) => handleShowLoading(async () => {
        await mentorCreateAction(data);
        navigate({ pathname: ROUTE_MENTOR_PATH.HOME });
      }),
      getAnswers: () => ({
        avatar: user.mentor?.avatar,
        headline: user.mentor?.headline,
        company: user.mentor?.company,
        country: user.mentor?.country,
        hours: user.mentor?.hours,
        visible: user.mentor?.visible,
        extra: user.mentor?.extra ?? {},
      }),
    },
  };
};

export default createOnboardingConfig;
